import { useMemo, useState } from 'react';

import { ArrowLeft, ArrowRight, Loader2, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';

import UserCard from '@/components/policyGroups/UserCard';

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

interface TransferListProps {
  availableUsers: User[];
  groupMembers: User[];
  onAddUsers: (userIds: number[]) => Promise<void>;
  onRemoveUsers: (userIds: number[]) => Promise<void>;
  isLoading?: boolean;
  isAdding?: boolean;
  isRemoving?: boolean;
}

const TransferList = ({
  availableUsers,
  groupMembers,
  onAddUsers,
  onRemoveUsers,
  isLoading = false,
  isAdding = false,
  isRemoving = false,
}: TransferListProps) => {
  // Selection state
  const [selectedAvailable, setSelectedAvailable] = useState<Set<number>>(new Set());
  const [selectedMembers, setSelectedMembers] = useState<Set<number>>(new Set());

  // Search state
  const [availableSearch, setAvailableSearch] = useState('');
  const [membersSearch, setMembersSearch] = useState('');

  // Filter users based on search
  const filteredAvailable = useMemo(() => {
    if (!availableSearch) return availableUsers;
    const search = availableSearch.toLowerCase();
    return availableUsers.filter(
      (user) =>
        user.first_name.toLowerCase().includes(search) ||
        user.last_name.toLowerCase().includes(search) ||
        user.email.toLowerCase().includes(search),
    );
  }, [availableUsers, availableSearch]);

  const filteredMembers = useMemo(() => {
    if (!membersSearch) return groupMembers;
    const search = membersSearch.toLowerCase();
    return groupMembers.filter(
      (user) =>
        user.first_name.toLowerCase().includes(search) ||
        user.last_name.toLowerCase().includes(search) ||
        user.email.toLowerCase().includes(search),
    );
  }, [groupMembers, membersSearch]);

  // Handle individual selection
  const handleAvailableSelect = (id: number, selected: boolean) => {
    const newSet = new Set(selectedAvailable);
    if (selected) {
      newSet.add(id);
    } else {
      newSet.delete(id);
    }
    setSelectedAvailable(newSet);
  };

  const handleMemberSelect = (id: number, selected: boolean) => {
    const newSet = new Set(selectedMembers);
    if (selected) {
      newSet.add(id);
    } else {
      newSet.delete(id);
    }
    setSelectedMembers(newSet);
  };

  // Handle select all
  const handleSelectAllAvailable = (checked: boolean) => {
    if (checked) {
      setSelectedAvailable(new Set(filteredAvailable.map((u) => u.id)));
    } else {
      setSelectedAvailable(new Set());
    }
  };

  const handleSelectAllMembers = (checked: boolean) => {
    if (checked) {
      setSelectedMembers(new Set(filteredMembers.map((u) => u.id)));
    } else {
      setSelectedMembers(new Set());
    }
  };

  // Check if all are selected
  const allAvailableSelected =
    filteredAvailable.length > 0 && filteredAvailable.every((u) => selectedAvailable.has(u.id));
  const allMembersSelected =
    filteredMembers.length > 0 && filteredMembers.every((u) => selectedMembers.has(u.id));

  // Handle transfers
  const handleAddSelected = async () => {
    if (selectedAvailable.size === 0) return;

    try {
      await onAddUsers(Array.from(selectedAvailable));
      setSelectedAvailable(new Set());
    } catch (error) {
      // Error handled by parent
    }
  };

  const handleRemoveSelected = async () => {
    if (selectedMembers.size === 0) return;

    try {
      await onRemoveUsers(Array.from(selectedMembers));
      setSelectedMembers(new Set());
    } catch (error) {
      // Error handled by parent
    }
  };

  const isOperating = isAdding || isRemoving;

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
      {/* Available Users Panel */}
      <div className='space-y-3'>
        <div className='flex items-center justify-between'>
          <h3 className='text-lg font-semibold'>Available Users</h3>
          <span className='text-sm text-muted-foreground'>
            {filteredAvailable.length} user{filteredAvailable.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Search */}
        <div className='relative'>
          <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
          <Input
            placeholder='Search users...'
            value={availableSearch}
            onChange={(e) => setAvailableSearch(e.target.value)}
            className='pl-9'
            disabled={isLoading || isOperating}
          />
        </div>

        {/* Select All */}
        <div className='flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg'>
          <Checkbox
            checked={allAvailableSelected}
            onCheckedChange={handleSelectAllAvailable}
            disabled={isLoading || isOperating || filteredAvailable.length === 0}
          />
          <span className='text-sm font-medium'>Select All</span>
        </div>

        {/* User List */}
        <div className='border rounded-lg p-3 bg-gray-50 min-h-[400px] max-h-[500px] overflow-y-auto space-y-2'>
          {isLoading ? (
            <div className='flex items-center justify-center h-40'>
              <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
            </div>
          ) : filteredAvailable.length === 0 ? (
            <div className='flex items-center justify-center h-40 text-sm text-muted-foreground'>
              {availableSearch ? 'No users match your search' : 'No available users'}
            </div>
          ) : (
            filteredAvailable.map((user) => (
              <UserCard
                key={user.id}
                id={user.id}
                firstName={user.first_name}
                lastName={user.last_name}
                email={user.email}
                selected={selectedAvailable.has(user.id)}
                onSelect={handleAvailableSelect}
                disabled={isOperating}
              />
            ))
          )}
        </div>

        {/* Add Button */}
        <Button
          onClick={handleAddSelected}
          disabled={selectedAvailable.size === 0 || isOperating}
          className='w-full'
        >
          {isAdding ? (
            <>
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              Adding...
            </>
          ) : (
            <>
              <ArrowRight className='mr-2 h-4 w-4' />
              Add Selected ({selectedAvailable.size})
            </>
          )}
        </Button>
      </div>

      {/* Group Members Panel */}
      <div className='space-y-3'>
        <div className='flex items-center justify-between'>
          <h3 className='text-lg font-semibold'>Group Members</h3>
          <span className='text-sm text-muted-foreground'>
            {filteredMembers.length} member{filteredMembers.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Search */}
        <div className='relative'>
          <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
          <Input
            placeholder='Search members...'
            value={membersSearch}
            onChange={(e) => setMembersSearch(e.target.value)}
            className='pl-9'
            disabled={isLoading || isOperating}
          />
        </div>

        {/* Select All */}
        <div className='flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg'>
          <Checkbox
            checked={allMembersSelected}
            onCheckedChange={handleSelectAllMembers}
            disabled={isLoading || isOperating || filteredMembers.length === 0}
          />
          <span className='text-sm font-medium'>Select All</span>
        </div>

        {/* User List */}
        <div className='border rounded-lg p-3 bg-gray-50 min-h-[400px] max-h-[500px] overflow-y-auto space-y-2'>
          {isLoading ? (
            <div className='flex items-center justify-center h-40'>
              <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className='flex items-center justify-center h-40 text-sm text-muted-foreground'>
              {membersSearch ? 'No members match your search' : 'No members in this group'}
            </div>
          ) : (
            filteredMembers.map((user) => (
              <UserCard
                key={user.id}
                id={user.id}
                firstName={user.first_name}
                lastName={user.last_name}
                email={user.email}
                selected={selectedMembers.has(user.id)}
                onSelect={handleMemberSelect}
                disabled={isOperating}
              />
            ))
          )}
        </div>

        {/* Remove Button */}
        <Button
          onClick={handleRemoveSelected}
          disabled={selectedMembers.size === 0 || isOperating}
          variant='destructive'
          className='w-full'
        >
          {isRemoving ? (
            <>
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              Removing...
            </>
          ) : (
            <>
              <ArrowLeft className='mr-2 h-4 w-4' />
              Remove Selected ({selectedMembers.size})
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default TransferList;
